import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, Camera, Sparkles, Loader2, X, ArrowRight, ArrowLeft, CheckCircle2, Link2, AlertTriangle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { dealerListings, buildImageUrl } from '../../services/dealerApi';
import { toast } from '../../hooks/use-toast';
import { compressForUpload } from '../../utils/imageCompress';

const inputCls = 'bg-slate-950/60 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500 focus-visible:ring-0';
const labelCls = 'block text-xs uppercase tracking-wider text-slate-400 mb-2';

const AddCar = () => {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [step, setStep] = useState(1); // 1: photos, 2: details, 3: AI processing
  const [photos, setPhotos] = useState([]); // [{ url }]
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0, percent: 0 });
  const [details, setDetails] = useState({ year: '', fuel_type: '', price: '', km_driven: '', ownership: '' });
  const [analyzing, setAnalyzing] = useState(false);
  const [importUrl, setImportUrl] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null); // { blocked, message, detected_from, fields_filled }

  const handleImportFromUrl = async () => {
    const url = importUrl.trim();
    if (!url) {
      toast({ title: 'Paste a URL first', variant: 'destructive' });
      return;
    }
    setImporting(true);
    setImportResult(null);
    try {
      const res = await dealerListings.importFromUrl(url);
      if (res.blocked || !res.success) {
        setImportResult({ blocked: true, message: res.message || "Couldn't import — please fill in manually." });
        return;
      }
      const d = res.details || {};
      const imported = res.photos || [];
      // Prefill only empty fields — never clobber what the dealer already typed
      setDetails((cur) => ({
        year: cur.year || (d.year ? String(d.year) : ''),
        fuel_type: cur.fuel_type || d.fuel_type || '',
        price: cur.price || (d.price ? String(d.price) : ''),
        km_driven: cur.km_driven || (d.km_driven ? String(d.km_driven) : ''),
        ownership: cur.ownership || (d.ownership ? String(d.ownership) : ''),
      }));
      // Append photos, respecting the 12-photo ceiling
      setPhotos((prev) => {
        const merged = [...prev];
        for (const p of imported) {
          if (merged.length >= 12) break;
          if (p.url && !merged.some((x) => x.url === p.url)) merged.push(p);
        }
        return merged;
      });
      const filled = [
        d.year && 'Year',
        d.fuel_type && 'Fuel',
        d.price && 'Price',
        d.km_driven && 'KMs',
        d.ownership && 'Owner',
      ].filter(Boolean);
      setImportResult({
        blocked: false,
        photos_added: imported.length,
        fields_filled: filled,
        detected_from: res.detected_from || [],
      });
      toast({
        title: `Imported ${imported.length} photo${imported.length === 1 ? '' : 's'} + ${filled.length} field${filled.length === 1 ? '' : 's'}`,
        description: 'Review below and adjust anything that looks off.',
      });
    } catch (err) {
      setImportResult({ blocked: true, message: err.response?.data?.detail || 'Import failed — please fill in manually.' });
    } finally {
      setImporting(false);
    }
  };

  const handleFiles = async (filesList) => {
    const files = Array.from(filesList || []);
    if (!files.length) return;
    if (photos.length + files.length > 12) {
      toast({ title: 'Too many photos', description: 'Maximum 12 photos per listing', variant: 'destructive' });
      return;
    }
    setUploading(true);
    setUploadProgress({ done: 0, total: files.length, percent: 0 });
    const failed = [];
    // ONE photo per HTTP request. A single slow / corrupt / oversized file
    // can never tank the whole batch (was causing prod CF 524 timeouts).
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const compressed = await compressForUpload(file);
        const res = await dealerListings.uploadSinglePhoto(compressed, {
          onProgress: (pct) => setUploadProgress({ done: i, total: files.length, percent: pct }),
        });
        if (res.photos?.[0]?.url) {
          setPhotos((prev) => [...prev, res.photos[0]]);
        } else if (res.errors?.[0]) {
          failed.push({ name: file.name, err: res.errors[0].error });
        }
      } catch (err) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        let msg = detail || err?.message || 'Upload failed';
        if (status === 524 || /timeout/i.test(msg)) msg = 'Slow network — try smaller photo';
        if (status === 413) msg = 'File too large (max 40 MB)';
        failed.push({ name: file.name, err: msg });
      }
      setUploadProgress({ done: i + 1, total: files.length, percent: 100 });
    }
    setUploading(false);
    setUploadProgress({ done: 0, total: 0, percent: 0 });
    if (failed.length) {
      toast({
        title: `${failed.length} of ${files.length} photos failed`,
        description: failed[0].err,
        variant: 'destructive',
      });
    }
  };

  const removePhoto = (url) => setPhotos((prev) => prev.filter((p) => p.url !== url));

  const handleAnalyze = async () => {
    if (!details.year || !details.fuel_type || !details.price) {
      toast({ title: 'Missing details', description: 'Year, fuel and price are required', variant: 'destructive' });
      return;
    }
    if (photos.length === 0) {
      toast({ title: 'No photos', description: 'Upload at least one photo', variant: 'destructive' });
      return;
    }
    setAnalyzing(true);
    try {
      const draft = await dealerListings.createDraft({
        year: parseInt(details.year),
        fuel_type: details.fuel_type,
        price: parseFloat(details.price),
        km_driven: details.km_driven ? parseInt(details.km_driven) : null,
        ownership: details.ownership ? parseInt(details.ownership) : null,
        photos,
      });
      const listingId = draft.listing_id;
      // Run AI
      await dealerListings.analyze(listingId);
      toast({ title: 'AI analysis complete', description: 'Review and approve before publishing' });
      navigate(`/dealers/inventory/${listingId}/approve`);
    } catch (err) {
      toast({ title: 'AI analysis failed', description: err.response?.data?.detail || 'Try again', variant: 'destructive' });
      setAnalyzing(false);
    }
  };

  return (
    <div data-testid="add-car-page" className="space-y-6">
      <div>
        <span className="text-xs uppercase tracking-[0.2em] text-cyan-400 font-medium">Add Car</span>
        <h1 className="mt-1 text-3xl md:text-4xl font-extrabold text-white tracking-tight">List a new car</h1>
        <p className="mt-1 text-slate-400 text-sm">Upload photos. We&apos;ll do the rest.</p>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-3 text-xs">
        {[
          { n: 1, label: 'Photos' },
          { n: 2, label: 'Details' },
          { n: 3, label: 'AI Magic' },
        ].map((s, idx) => (
          <React.Fragment key={s.n}>
            <div className={`flex items-center gap-2 ${step >= s.n ? 'text-cyan-300' : 'text-slate-500'}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold ${step >= s.n ? 'bg-cyan-500 text-slate-950' : 'bg-slate-800 text-slate-400'}`}>
                {step > s.n ? <CheckCircle2 className="w-4 h-4" /> : s.n}
              </div>
              <span className="uppercase tracking-wider">{s.label}</span>
            </div>
            {idx < 2 && <div className={`flex-1 h-px ${step > s.n ? 'bg-cyan-500/40' : 'bg-slate-800'}`} />}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Photo Upload */}
      {step === 1 && (
        <div className="bg-slate-900/40 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 space-y-4">
          {/* Import from URL — CarWale / CarTrade / OLX / any listing site */}
          <div className="bg-gradient-to-br from-emerald-500/10 to-cyan-500/5 border border-emerald-500/30 rounded-2xl p-4 space-y-3" data-testid="import-url-tile">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center flex-shrink-0">
                <Link2 className="w-4 h-4 text-emerald-300" />
              </div>
              <div className="flex-1">
                <div className="text-sm font-bold text-white flex items-center gap-2">
                  Import from a listing URL
                  <span className="text-[10px] uppercase tracking-widest text-emerald-300 bg-emerald-500/15 border border-emerald-500/30 px-2 py-0.5 rounded-full">Beta</span>
                </div>
                <div className="text-xs text-slate-400 mt-0.5">
                  Paste a CarWale / CarTrade / OLX / CarDekho listing link — we&apos;ll pull photos and details automatically.
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="url"
                value={importUrl}
                onChange={(e) => setImportUrl(e.target.value)}
                placeholder="https://www.carwale.com/used-cars-details/..."
                data-testid="import-url-input"
                disabled={importing}
                className="flex-1 px-3 py-2.5 rounded-lg bg-slate-950/70 border border-slate-700 text-white text-sm placeholder-slate-500 focus:border-emerald-500 outline-none disabled:opacity-50"
              />
              <button
                onClick={handleImportFromUrl}
                disabled={importing || !importUrl.trim()}
                data-testid="import-url-btn"
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {importing ? <><Loader2 className="w-4 h-4 animate-spin" /> Importing…</> : <><Link2 className="w-4 h-4" /> Import</>}
              </button>
            </div>
            {importResult?.blocked && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-xs" data-testid="import-blocked-msg">
                <AlertTriangle className="w-4 h-4 text-amber-300 flex-shrink-0 mt-0.5" />
                <div className="text-amber-200">{importResult.message}</div>
              </div>
            )}
            {importResult && !importResult.blocked && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-xs" data-testid="import-success-msg">
                <CheckCircle2 className="w-4 h-4 text-emerald-300 flex-shrink-0 mt-0.5" />
                <div className="text-emerald-100">
                  Imported <b>{importResult.photos_added}</b> photo{importResult.photos_added === 1 ? '' : 's'}
                  {importResult.fields_filled?.length > 0 && <> · Filled: <b>{importResult.fields_filled.join(', ')}</b></>}
                  <div className="text-emerald-300/70 mt-0.5">Review below and adjust anything that&apos;s off before running AI.</div>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-3 text-xs text-slate-500">
            <div className="flex-1 h-px bg-slate-800" />
            <span className="uppercase tracking-widest">or upload photos yourself</span>
            <div className="flex-1 h-px bg-slate-800" />
          </div>

          <h2 className="text-lg font-semibold text-white">Upload car photos</h2>
          <p className="text-sm text-slate-400">Front, rear, sides, dashboard, instrument cluster, interior. The more, the better.</p>

          <div
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
            className="border-2 border-dashed border-slate-700 hover:border-cyan-500/40 rounded-2xl p-8 text-center cursor-pointer transition-colors"
            data-testid="photo-dropzone"
          >
            <Camera className="w-10 h-10 text-cyan-400 mx-auto mb-3" strokeWidth={1.5} />
            <p className="text-white font-semibold">Tap to take photos or upload from gallery</p>
            <p className="text-xs text-slate-400 mt-1">JPG, PNG, HEIC · Up to 12 photos</p>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              data-testid="photo-input"
              onChange={(e) => handleFiles(e.target.files)}
            />
          </div>

          {uploading && (
            <div className="space-y-2" data-testid="upload-progress">
              <div className="flex items-center justify-between text-xs">
                <span className="text-cyan-400 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Uploading {uploadProgress.done + 1} of {uploadProgress.total}…
                </span>
                <span className="text-slate-400 font-mono">{uploadProgress.percent}%</span>
              </div>
              <div className="h-1.5 w-full bg-slate-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-cyan-500 transition-all"
                  style={{ width: `${Math.round(((uploadProgress.done * 100) + uploadProgress.percent) / Math.max(uploadProgress.total, 1))}%` }}
                />
              </div>
            </div>
          )}

          {photos.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
              {photos.map((p) => (
                <div key={p.url} className="relative aspect-square bg-slate-800 rounded-lg overflow-hidden group">
                  <img src={buildImageUrl(p.url)} alt="" className="w-full h-full object-cover" />
                  <button
                    onClick={() => removePhoto(p.url)}
                    className="absolute top-1 right-1 w-6 h-6 rounded-full bg-slate-950/80 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                    data-testid="remove-photo-btn"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button
              onClick={() => setStep(2)}
              disabled={photos.length === 0}
              data-testid="step1-next-btn"
              className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold rounded-xl"
            >
              Next <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Details */}
      {step === 2 && (
        <div className="bg-slate-900/40 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 space-y-4">
          <h2 className="text-lg font-semibold text-white">Tell us about the car</h2>
          <p className="text-sm text-slate-400">Just a few facts. AI will figure out the rest.</p>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Year *</label>
              <Input data-testid="car-year-input" type="number" min="1990" max={new Date().getFullYear()} placeholder="2020" value={details.year} onChange={(e) => setDetails({ ...details, year: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Fuel Type *</label>
              <Select value={details.fuel_type} onValueChange={(v) => setDetails({ ...details, fuel_type: v })}>
                <SelectTrigger data-testid="car-fuel-select" className="bg-slate-950/60 border-slate-700 text-white"><SelectValue placeholder="Select fuel" /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  {['Petrol', 'Diesel', 'CNG', 'Electric', 'Hybrid'].map((f) => (
                    <SelectItem key={f} value={f} className="text-slate-200 focus:bg-slate-800 focus:text-white">{f}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className={labelCls}>Price (₹) *</label>
              <Input data-testid="car-price-input" type="number" min="10000" placeholder="500000" value={details.price} onChange={(e) => setDetails({ ...details, price: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>KM Driven (optional)</label>
              <Input data-testid="car-km-input" type="number" min="0" placeholder="45000" value={details.km_driven} onChange={(e) => setDetails({ ...details, km_driven: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Number of Owners (optional)</label>
              <select
                data-testid="car-ownership-input"
                value={details.ownership}
                onChange={(e) => setDetails({ ...details, ownership: e.target.value })}
                className={inputCls}
              >
                <option value="">Select…</option>
                <option value="1">1st owner</option>
                <option value="2">2nd owner</option>
                <option value="3">3rd owner</option>
                <option value="4">4th owner</option>
                <option value="5">5+ owners</option>
              </select>
            </div>
          </div>

          <div className="flex justify-between pt-2">
            <Button onClick={() => setStep(1)} variant="outline" className="border-slate-700 bg-slate-900/40 text-slate-200">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <Button
              onClick={() => { setStep(3); handleAnalyze(); }}
              disabled={!details.year || !details.fuel_type || !details.price}
              data-testid="step2-analyze-btn"
              className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-slate-950 font-bold rounded-xl"
            >
              <Sparkles className="w-4 h-4 mr-2" /> Run AI
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: AI Processing */}
      {step === 3 && (
        <div className="bg-slate-900/40 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-12 text-center space-y-4">
          <div className="inline-flex w-16 h-16 rounded-full bg-cyan-500/10 border border-cyan-500/30 items-center justify-center">
            <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
          </div>
          <h2 className="text-xl font-bold text-white">AI is analyzing your car</h2>
          <div className="text-sm text-slate-400 space-y-1">
            <p className="flex items-center justify-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" /> Detecting make, model and variant</p>
            <p className="flex items-center justify-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" style={{ animationDelay: '0.3s' }} /> Generating professional description</p>
            <p className="flex items-center justify-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" style={{ animationDelay: '0.6s' }} /> Spotting visible damages</p>
          </div>
          <p className="text-xs text-slate-500">This usually takes 15-30 seconds</p>
        </div>
      )}
    </div>
  );
};

export default AddCar;
